
import React, { useState, useRef } from 'react';
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import EntitiesPopup from "layouts/components/EntitiesPopup";
import AudienceChangeGainLossChart from "./AudienceChangeGainLossChart"; // ✅ Import new component

// -----------------------------------------------------------
// 🚀 HELPER FUNCTION
// -----------------------------------------------------------

/**
 * Gets the follower/subscriber value for a single data item,
 * prioritizing the actual value and falling back to the predicted value.
 * Returns both the value and a flag if it was predicted.
 */
const getFollowerValue = (item, metricKey, predictedMetricKey) => {
  if (!item) {
    // If the item itself doesn't exist (e.g., no previous day), return null
    return { value: null, isPredicted: false };
  }

  const actual = item[metricKey];
  const predicted = item[predictedMetricKey];

  // Priority 1: Use actual value if it's positive
  if (actual != null && actual > 0) {
    return { value: actual, isPredicted: false };
  }
  // Priority 2: Use predicted value if it's positive
  if (predicted != null && predicted > 0) {
    return { value: predicted, isPredicted: true };
  }

  // Handle 0 values (e.g., start of a profile)
  // Prefer actual '0'
  if (actual != null) {
    return { value: actual, isPredicted: false };
  }
  // Fallback to predicted '0'
  if (predicted != null) {
    return { value: predicted, isPredicted: true };
  }

  // If no data (both are null/undefined), return null
  return { value: null, isPredicted: false };
};

// -----------------------------------------------------------
// 🚀 MAIN COMPONENT
// -----------------------------------------------------------

function AudienceChangeChart({
  title,
  datasets,
  previousdatasets,
  futuredatasets = [],
  adjustedcurrentdatasets,
  adjustedpreviousdatasets,
  isPredictionEnabled = false,
  isLoading = false,
  entitlements, // ✅ Added prop
  viewMode, // ✅ NEW
  onViewModeChange, // ✅ NEW
  profileColorMap // ✅ Global Color Map
}) {

  // Safely fallback if datasets is null
  const safeDatasets = datasets || [];
  // ✅ MOVED: Define previous datasets early so it's accessible in the conditional return
  const safePreviousDatasets = previousdatasets || [];

  // 🚀 SWITCHER LOGIC: Check if we have granular data (IG Own or FB Own or YouTube Own)
  const hasGranularData = safeDatasets.length > 0 && safeDatasets.every(p =>
    (p.platform === 'Instagram' || p.platform === 'Facebook' || p.platform === 'YouTube') &&
    p.data &&
    p.data.some(d =>
      (d.profileMetrics?.followers_gain !== undefined) ||
      (d.profileMetrics?.follower_gain !== undefined) || // ✅ Legacy Support
      (d.pageMetrics?.followers_gain !== undefined) ||
      (d.channelMetrics?.subscribersGained !== undefined) // ✅ YouTube Support
    )
  );

  if (hasGranularData && safeDatasets.length === 1) {
    return (
      <AudienceChangeGainLossChart
        title={title}
        datasets={safeDatasets}
        previousdatasets={safePreviousDatasets} // ✅ Pass Previous Data
        isLoading={isLoading}
        entitlements={entitlements}
        viewMode={viewMode}
        onViewModeChange={onViewModeChange}
        profileColorMap={profileColorMap} // ✅ Pass Global Color Map
      />
    );
  }

  // ✅ State for the popup
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupDate, setPopupDate] = useState(null);

  // ✅ Click handlers for the popup
  const handleBarClick = (date) => {
    setPopupDate(date);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupDate(null);
  };

  // ---------------------------------------------------------------------
  // RENDER LOADER
  // ---------------------------------------------------------------------
  // This renders ON TOP of the chart, so the chart doesn't unmount
  const renderLoader = () => (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(255, 255, 255, 0.7)",
        zIndex: 10,
        borderRadius: "0.75rem",
      }}
    >
      <CircularProgress size={32} thickness={4} color="accent" />
    </MDBox>
  );

  // (Title generation logic)
  const platformSet = new Set(datasets ? datasets.map((profile) => profile.platform) : []);
  let derivedTitle = '';

  if (platformSet.size === 1) {
    const platform = platformSet.values().next().value;
    derivedTitle = platform === "YouTube" ? "Subscribers Change Over Time" : "Followers Change Over Time";
  } else if (platformSet.size > 1) {
    const hasYouTube = [...platformSet].includes("YouTube");
    derivedTitle = hasYouTube ? "Subscribers Change Over Time" : "Followers Change Over Time";
  } else {
    derivedTitle = "Followers Change Over Time"; // Fallback
  }

  // Safely fallback if datasets is null (e.g. during initial load)
  // safeDatasets is already computed at top
  // safePreviousDatasets is already computed at top
  const safeFutureDatasets = futuredatasets || [];
  const safeAdjustedCurrent = adjustedcurrentdatasets || [];
  const safeAdjustedPrevious = adjustedpreviousdatasets || [];

  // ✅ Separate helpers for adjusted datasets
  const findAdjustedCurrentProfile = (profileId) =>
    safeAdjustedCurrent.find((p) => p.profileId === profileId);

  const findAdjustedPreviousProfile = (profileId) =>
    safeAdjustedPrevious.find((p) => p.profileId === profileId);

  // ✅ Use provided Future Data if prediction is enabled
  let futureData = isPredictionEnabled && safeFutureDatasets ? safeFutureDatasets : [];

  // ✅ Align labels
  const actualLabels = safeDatasets.length > 0 ? safeDatasets[0].data.map((item) => item.date) : [];
  const predictedLabels = futureData.length > 0 && safeDatasets.length > 0
    ? futureData[0].data.slice(safeDatasets[0].data.length).map((item) => item.date)
    : [];
  const chartLabels = [...actualLabels, ...predictedLabels];

  // ⬇️=========================================
  // ⬇️ CALCULATION FUNCTION
  // ⬇️=========================================
  const calculateFollowerChange = (profile, adjustedDatasetFinder) => {
    const adjustedProfile = adjustedDatasetFinder(profile.profileId);

    // Define all 4 keys
    const isYouTube = profile.platform === "YouTube";
    const metricKey = isYouTube ? "subscribers" : "followers";
    const predictedMetricKey = isYouTube ? "predicted_subscribers" : "predicted_followers";

    const changeData = [];
    const predictionFlags = [];

    profile.data.forEach((item, index) => {
      // 1. Get Current Day's Value
      const currentValue = getFollowerValue(item, metricKey, predictedMetricKey);

      // 2. Get Previous Day's Value
      let previousItem = null;
      if (index === 0 && adjustedProfile) {
        // Special case: Use the adjusted data for the very first day
        previousItem = adjustedProfile.data[0];
      } else if (index > 0) {
        // Normal case: Use the previous day from the main data array
        previousItem = profile.data[index - 1];
      }

      const previousValue = getFollowerValue(previousItem, metricKey, predictedMetricKey);

      // 3. Calculate Change and Flag
      let change = null;
      let flag = false;

      // Only calculate change if we have two valid numbers
      if (currentValue.value !== null && previousValue.value !== null) {
        change = currentValue.value - previousValue.value;
        // Flag as "predicted" if EITHER the current or previous value was a fallback
        flag = currentValue.isPredicted || previousValue.isPredicted;
      }

      changeData.push(change);
      predictionFlags.push(flag);
    });

    return { data: changeData, isPredictedFlags: predictionFlags };
  };
  // ⬆️=========================================

  // ✅ Construct Chart Data
  const computedChartData = {
    labels: chartLabels,
    datasets: [
      // 🔹 Actual Data
      ...safeDatasets.map((profile) => {
        const { data, isPredictedFlags } = calculateFollowerChange(profile, findAdjustedCurrentProfile);
        return {
          label: profile.name || profile.label,
          data: data,
          isPredictedFlags: isPredictedFlags,
          profileId: profile.profileId,
          profilePictureUrl: profile.profilePictureUrl || '',
          platform: profile.platform || "Unknown",
          metricName:
            profile.platform === "YouTube"
              ? "Subscribers Change"
              : "Followers Change",
        };
      }),

      // 🔹 Predicted Data
      ...futureData.map((profile) => {
        const { data, isPredictedFlags } = calculateFollowerChange(profile, findAdjustedCurrentProfile);
        return {
          label: `${profile.name || profile.label} (Predicted)`,
          data: data,
          isPredictedFlags: isPredictedFlags,
          profileId: profile.profileId,
          profilePictureUrl: profile.profilePictureUrl || '',
          platform: profile.platform || "Unknown",
          metricName:
            profile.platform === "YouTube"
              ? "Predicted Subscribers Change"
              : "Predicted Followers Change",
        };
      }),
    ],

    // 🔹 Previous Data
    previousdatasets: safePreviousDatasets.map((profile) => {
      const { data, isPredictedFlags } = calculateFollowerChange(profile, findAdjustedPreviousProfile);
      return {
        label: profile.name || profile.label,
        data: data,
        isPredictedFlags: isPredictedFlags,
        profileId: profile.profileId,
        profilePictureUrl: profile.profilePictureUrl || '',
        platform: profile.platform || "Unknown",
        metricName:
          profile.platform === "YouTube"
            ? "Subscribers Change"
            : "Followers Change",
      };
    }),
  };

  // -----------------------------------------------------------
  // PERSISTENCE LOGIC
  // -----------------------------------------------------------
  // We use a Ref to store the last valid data to show during loading
  const lastChartDataRef = useRef(null);

  // If we have actual data, update the ref
  if (safeDatasets.length > 0) {
    lastChartDataRef.current = computedChartData;
  }

  let dataToRender = computedChartData;
  if (isLoading && safeDatasets.length === 0 && lastChartDataRef.current) {
    dataToRender = lastChartDataRef.current;
  }

  const hasData = (dataToRender.datasets && dataToRender.datasets.length > 0);

  // ✅ Tooltip for Insights
  // ✅ Tooltip for Insights
  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        This chart displays the net daily change in your audience.
        It highlights days of significant growth or loss.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" display="block" gutterBottom>
        - <strong>Positive Bars (Up)</strong>: You gained new followers on that day.<br />
        - <strong>Negative Bars (Down)</strong>: You lost followers on that day.<br />
        - <strong>Tall Bars</strong>: Indicates a viral moment or a drop-off event.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Identify which specific posts caused a surge in followers.<br />
        - Spot days with unusual follower loss to investigate causes.<br />
        - Correlate spikes with campaigns or viral content.<br />
        - Measure the immediate impact of your daily activity.
      </Typography>
    </div>
  );

  return (
    <div style={{ position: "relative" }}>
      {isLoading && renderLoader()}

      {hasData ? (
        <VerticalBarChart
          title={derivedTitle}
          description={tooltipContent}
          height="19.125rem"
          chart={dataToRender}
          aggregate={true}
          onBarClick={handleBarClick} // ✅ Pass click handler
          rawData={safeDatasets}
          entitlements={entitlements} // ✅ Pass entitlements
          viewMode={viewMode} // ✅ Pass down CORRECT PROP NAME
          onViewModeChange={onViewModeChange} // ✅ Pass down
          profileColorMap={profileColorMap} // ✅ Pass Global Color Map
        />
      ) : (
        <Card sx={{ height: "19.125rem", display: "flex", justifyContent: "center", alignItems: "center" }}>
          {!isLoading && (
            <MDTypography variant="body2" color="text">
              No follower change data available.
            </MDTypography>
          )}
        </Card>
      )}

      {isPopupOpen && (
        <EntitiesPopup
          isModalOpen={isPopupOpen}
          onClose={handlePopupClose}
          datasets={datasets}
          date={popupDate}
        />
      )}
    </div>
  );
}

// 🛡️ PropTypes Validation
AudienceChangeChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  datasets: PropTypes.array.isRequired,
  previousdatasets: PropTypes.array.isRequired,
  futuredatasets: PropTypes.array,
  adjustedcurrentdatasets: PropTypes.array.isRequired,
  adjustedpreviousdatasets: PropTypes.array.isRequired,
  isPredictionEnabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  entitlements: PropTypes.object,
  viewMode: PropTypes.string,
  onViewModeChange: PropTypes.func,
  profileColorMap: PropTypes.object,
};

export default AudienceChangeChart;